import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Combobox } from "../../ui/combobox";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CampaignAccountCard from "./CampaignAccountCard";
import CampaignsViewCard from "./CampaignsViewCard";

/** A track's campaigns per ad account; editable for super admins, read-only for everyone else. */
const CampaignsEditor = ({ accounts, onChange, readOnly }) => {
  const axiosPrivate = useAxiosPrivate();
  const { data: metaAccounts = [], isLoading } = useQuery({
    queryKey: ["meta-ad-accounts"],
    queryFn: () => AdsAPIs.listMetaAdAccounts({ axiosPrivate }),
    staleTime: 5 * 60 * 1000,
    enabled: !readOnly,
  });
  const accountOptions = metaAccounts
    .filter((a) => !accounts.some((x) => x.meta_ad_account_id === a.id))
    .map((a) => ({ value: a.id, label: a.name || a.id }));

  const handleAddAccount = (id) => {
    const found = metaAccounts.find((a) => a.id === id);
    if (!found) return;
    onChange([
      ...accounts,
      {
        meta_ad_account_id: found.id,
        ad_account_name: found.name,
        campaigns: [],
      },
    ]);
  };

  const handleUpdateAccount = ({ accountId, campaigns }) =>
    onChange(
      accounts.map((a) =>
        a.meta_ad_account_id === accountId ? { ...a, campaigns } : a
      )
    );

  const handleRemoveAccount = ({ accountId }) =>
    onChange(accounts.filter((a) => a.meta_ad_account_id !== accountId));

  if (readOnly) {
    return (
      <div className="space-y-3">
        {accounts.length ? (
          accounts.map((a) => (
            <CampaignsViewCard key={a.meta_ad_account_id} account={a} />
          ))
        ) : (
          <p className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-500">
            No campaigns mapped.
          </p>
        )}
        <p className="text-xs text-gray-500">
          Only super admins can change campaigns.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {accounts.map((a) => (
        <CampaignAccountCard
          key={a.meta_ad_account_id}
          account={a}
          onChange={(campaigns) =>
            handleUpdateAccount({ accountId: a.meta_ad_account_id, campaigns })
          }
          onRemove={() =>
            handleRemoveAccount({ accountId: a.meta_ad_account_id })
          }
        />
      ))}
      {!accounts.length && (
        <p className="rounded-xl border border-dashed border-gray-200 px-4 py-5 text-center text-sm text-gray-500">
          No campaigns mapped yet. Add an ad account to start.
        </p>
      )}
      <Combobox
        value=""
        options={accountOptions}
        onChange={handleAddAccount}
        disabled={isLoading}
        placeholder={isLoading ? "Loading ad accounts…" : "+ Add ad account"}
        searchPlaceholder="Search ad account…"
        emptyText="No more ad accounts."
        className="h-10 border-dashed text-gray-500 hover:border-[#7f7ad5]"
      />
    </div>
  );
};

export default CampaignsEditor;
